/**
 * TestBriefPanel — the project owner's testing brief, captured once and read by
 * the agent before every run.
 *
 * This is the "you drive" surface: instead of the agent guessing scope, auth,
 * and what matters, you state it here and it becomes authoritative context.
 * Auth secrets are write-only — the API never returns them, only whether one is
 * stored.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  MenuItem, TextField, Typography, CircularProgress, Alert, Tooltip,
} from '@mui/material';
import { Assignment as BriefIcon } from '@mui/icons-material';
import {
  getTestBrief, saveTestBrief, TestBrief, TestBriefAccount,
} from '../services/project.service';
import { IconButton } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

interface Props {
  projectId?: string | number | null;
}

const EMPTY: TestBrief = {
  auth: { mode: 'none', login_url: '', username: '', token_location: '', secret: '' },
  accounts: [],
  environment: { base_url: '', api_base_url: '', api_auth: { header: '', token: '' } },
  guardrails: [],
  pages_include: [], pages_exclude: [], business_rules: [], assertions: [],
  seed_notes: '', priorities: '', notes: '',
};

const toText = (items?: string[]) => (items || []).join('\n');
const toList = (text: string) =>
  text.split('\n').map((s) => s.trim()).filter(Boolean);

const TestBriefPanel: React.FC<Props> = ({ projectId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState(false);

  const [brief, setBrief] = useState<TestBrief>(EMPTY);
  // List fields are edited as multiline text; kept separate to preserve typing.
  const [incl, setIncl] = useState('');
  const [excl, setExcl] = useState('');
  const [rules, setRules] = useState('');
  const [asserts, setAsserts] = useState('');
  const [guards, setGuards] = useState('');
  const [secretSet, setSecretSet] = useState(false);

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    getTestBrief(String(projectId))
      .then((b) => {
        setBrief({
          ...EMPTY, ...b,
          auth: { ...EMPTY.auth, ...b.auth, secret: '' },
          environment: {
            ...EMPTY.environment, ...b.environment,
            api_auth: { ...EMPTY.environment.api_auth, ...b.environment?.api_auth, token: '' },
          },
          accounts: (b.accounts || []).map((a) => ({ ...a, secret: '' })),
        });
        setIncl(toText(b.pages_include));
        setExcl(toText(b.pages_exclude));
        setRules(toText(b.business_rules));
        setAsserts(toText(b.assertions));
        setGuards(toText(b.guardrails));
        setSecretSet(!!b.auth?.secret_set);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleSave = async () => {
    if (!projectId) return;
    setSaving(true);
    setError(null);
    setSavedMsg(false);
    try {
      const payload: TestBrief = {
        ...brief,
        guardrails: toList(guards),
        pages_include: toList(incl),
        pages_exclude: toList(excl),
        business_rules: toList(rules),
        assertions: toList(asserts),
      };
      // Blank secrets => keep the stored ones (backend preserves them).
      if (!payload.auth.secret) delete payload.auth.secret;
      if (payload.environment?.api_auth && !payload.environment.api_auth.token) {
        delete payload.environment.api_auth.token;
      }
      payload.accounts = (payload.accounts || []).map((a) => {
        const c = { ...a };
        if (!c.secret) delete c.secret;
        return c;
      });
      const saved = await saveTestBrief(String(projectId), payload);
      setSecretSet(!!saved.auth?.secret_set);
      setBrief((b) => ({ ...b, auth: { ...b.auth, secret: '' } }));
      setSavedMsg(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const setAuth = (patch: Partial<TestBrief['auth']>) =>
    setBrief((b) => ({ ...b, auth: { ...b.auth, ...patch } }));
  const setEnv = (patch: Partial<TestBrief['environment']>) =>
    setBrief((b) => ({ ...b, environment: { ...b.environment, ...patch } }));
  const setApiAuth = (patch: Partial<NonNullable<TestBrief['environment']['api_auth']>>) =>
    setBrief((b) => ({
      ...b,
      environment: { ...b.environment, api_auth: { ...b.environment.api_auth, ...patch } },
    }));
  const setAccount = (i: number, patch: Partial<TestBriefAccount>) =>
    setBrief((b) => ({
      ...b,
      accounts: b.accounts.map((a, idx) => (idx === i ? { ...a, ...patch } : a)),
    }));
  const addAccount = () =>
    setBrief((b) => ({ ...b, accounts: [...b.accounts, { role: '', mode: 'test_account' }] }));
  const removeAccount = (i: number) =>
    setBrief((b) => ({ ...b, accounts: b.accounts.filter((_, idx) => idx !== i) }));

  if (!projectId) return null;

  return (
    <>
      <Tooltip title="Test Brief — tell the agent how to test this project">
        <Button
          size="small" variant="outlined" startIcon={<BriefIcon />}
          onClick={() => setOpen(true)} sx={{ ml: 1, textTransform: 'none' }}
        >
          Test Brief
        </Button>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BriefIcon fontSize="small" />
            <span>Test Brief</span>
          </Box>
          <Typography variant="caption" color="text.secondary">
            The agent reads this before every run. Tell it how to log in, what to
            test, and what to assert — so it uses your knowledge, not guesses.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          {savedMsg && <Alert severity="success" sx={{ mb: 1.5 }}>Saved.</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              {/* Auth */}
              <Typography variant="subtitle2" sx={{ mt: 0.5 }}>Authentication</Typography>
              <TextField
                select size="small" label="How to log in" value={brief.auth.mode}
                onChange={(e) => setAuth({ mode: e.target.value })}
              >
                <MenuItem value="none">No login needed</MenuItem>
                <MenuItem value="test_account">Test account (username + password)</MenuItem>
                <MenuItem value="otp">Phone + OTP</MenuItem>
                <MenuItem value="token">Injected session token</MenuItem>
                <MenuItem value="real_login">Drive the real login form</MenuItem>
              </TextField>
              {brief.auth.mode !== 'none' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  <TextField
                    size="small" label="Login URL"
                    value={brief.auth.login_url || ''}
                    onChange={(e) => setAuth({ login_url: e.target.value })}
                  />
                  {brief.auth.mode !== 'token' && brief.auth.mode !== 'otp' && (
                    <TextField
                      size="small" label="Username / email"
                      value={brief.auth.username || ''}
                      onChange={(e) => setAuth({ username: e.target.value })}
                    />
                  )}
                  {brief.auth.mode !== 'otp' && (
                    <TextField
                      size="small" type="password"
                      label={brief.auth.mode === 'token' ? 'Session token' : 'Password'}
                      placeholder={secretSet ? '•••••••• (stored — leave blank to keep)' : ''}
                      value={brief.auth.secret || ''}
                      onChange={(e) => setAuth({ secret: e.target.value })}
                    />
                  )}
                  {brief.auth.mode === 'token' && (
                    <TextField
                      size="small" label="Inject token at"
                      placeholder="localStorage:token  or  cookie:session"
                      value={brief.auth.token_location || ''}
                      onChange={(e) => setAuth({ token_location: e.target.value })}
                    />
                  )}
                  {brief.auth.mode === 'otp' && (
                    <>
                      <TextField
                        size="small" label="Phone number (entered on login)"
                        value={brief.auth.phone || ''}
                        onChange={(e) => setAuth({ phone: e.target.value })}
                      />
                      <TextField
                        select size="small" label="Where the OTP comes from"
                        value={brief.auth.otp_source || 'device_relay'}
                        onChange={(e) => setAuth({ otp_source: e.target.value })}
                      >
                        <MenuItem value="device_relay">Read SMS off a live device</MenuItem>
                        <MenuItem value="fixed_code">Fixed test-build code</MenuItem>
                        <MenuItem value="provider">OTP provider API</MenuItem>
                      </TextField>
                      {brief.auth.otp_source === 'device_relay' && (
                        <TextField
                          size="small" label="Device serial (the phone with the SIM)"
                          value={brief.auth.otp_device_serial || ''}
                          onChange={(e) => setAuth({ otp_device_serial: e.target.value })}
                        />
                      )}
                      {brief.auth.otp_source === 'fixed_code' && (
                        <TextField
                          size="small" label="Fixed OTP code"
                          value={brief.auth.otp_fixed_code || ''}
                          onChange={(e) => setAuth({ otp_fixed_code: e.target.value })}
                        />
                      )}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          size="small" type="number" label="OTP length" sx={{ width: 120 }}
                          value={brief.auth.otp_length ?? ''}
                          onChange={(e) => setAuth({ otp_length: e.target.value ? Number(e.target.value) : null })}
                        />
                        <TextField
                          select size="small" label="CAPTCHA" fullWidth
                          value={brief.auth.captcha_handling || 'none'}
                          onChange={(e) => setAuth({ captcha_handling: e.target.value })}
                        >
                          <MenuItem value="none">None</MenuItem>
                          <MenuItem value="test_key">Test-key build (auto-pass)</MenuItem>
                          <MenuItem value="hitl">Human solves in live view</MenuItem>
                        </TextField>
                      </Box>
                    </>
                  )}
                </Box>
              )}

              <Divider />

              {/* Extra accounts / roles */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Extra accounts (roles)</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addAccount} sx={{ textTransform: 'none' }}>
                  Add
                </Button>
              </Box>
              {brief.accounts.map((acc, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small" label="Role" sx={{ width: 120 }}
                    value={acc.role}
                    onChange={(e) => setAccount(i, { role: e.target.value })}
                  />
                  <TextField
                    size="small" label="Username / phone" fullWidth
                    value={acc.username || acc.phone || ''}
                    onChange={(e) => setAccount(i, { username: e.target.value })}
                  />
                  <TextField
                    size="small" type="password" label="Secret" sx={{ width: 140 }}
                    placeholder={acc.secret_set ? '•••• (stored)' : ''}
                    value={acc.secret || ''}
                    onChange={(e) => setAccount(i, { secret: e.target.value })}
                  />
                  <IconButton size="small" onClick={() => removeAccount(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}

              <Divider />

              {/* Environment */}
              <Typography variant="subtitle2">Environment</Typography>
              <TextField
                size="small" label="App base URL (in-cluster / reachable)"
                placeholder="http://tms-frontend.cloudtest.svc"
                value={brief.environment.base_url || ''}
                onChange={(e) => setEnv({ base_url: e.target.value })}
              />
              <TextField
                size="small" label="API base URL"
                value={brief.environment.api_base_url || ''}
                onChange={(e) => setEnv({ api_base_url: e.target.value })}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small" label="API auth header" sx={{ width: 160 }}
                  placeholder="Authorization"
                  value={brief.environment.api_auth?.header || ''}
                  onChange={(e) => setApiAuth({ header: e.target.value })}
                />
                <TextField
                  size="small" type="password" label="API token" fullWidth
                  placeholder={brief.environment.api_auth?.token_set ? '•••• (stored)' : ''}
                  value={brief.environment.api_auth?.token || ''}
                  onChange={(e) => setApiAuth({ token: e.target.value })}
                />
              </Box>

              <Divider />

              {/* Scope */}
              <Typography variant="subtitle2">Scope</Typography>
              <TextField
                size="small" label="Pages / flows to test (one per line)" multiline minRows={2}
                value={incl} onChange={(e) => setIncl(e.target.value)}
              />
              <TextField
                size="small" label="Skip / never test (one per line)" multiline minRows={1}
                value={excl} onChange={(e) => setExcl(e.target.value)}
              />

              <Divider />

              {/* What matters */}
              <Typography variant="subtitle2">What to check</Typography>
              <TextField
                size="small" label="Business rules to uphold (one per line)" multiline minRows={2}
                placeholder="Deactivating a user must invalidate their session"
                value={rules} onChange={(e) => setRules(e.target.value)}
              />
              <TextField
                size="small" label="Assertions to make (one per line)" multiline minRows={2}
                value={asserts} onChange={(e) => setAsserts(e.target.value)}
              />
              <TextField
                size="small" label="Hard guardrails — never do these (one per line)" multiline minRows={1}
                placeholder="Never delete real user data"
                value={guards} onChange={(e) => setGuards(e.target.value)}
              />

              <Divider />

              {/* Extra */}
              <TextField
                size="small" label="Data / seeding notes" multiline minRows={1}
                value={brief.seed_notes || ''}
                onChange={(e) => setBrief((b) => ({ ...b, seed_notes: e.target.value }))}
              />
              <TextField
                size="small" label="Priorities" multiline minRows={1}
                value={brief.priorities || ''}
                onChange={(e) => setBrief((b) => ({ ...b, priorities: e.target.value }))}
              />
              <TextField
                size="small" label="Notes" multiline minRows={1}
                value={brief.notes || ''}
                onChange={(e) => setBrief((b) => ({ ...b, notes: e.target.value }))}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
          <Button
            variant="contained" onClick={handleSave} disabled={saving || loading}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Saving…' : 'Save brief'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TestBriefPanel;
